import type { Dish } from "@/api/Dishes/DishesAPI.model";
import type { DishSize } from "@/api/Sizes/SizesAPI.model";
import type { Topping } from "@/api/Toppings/ToppingsAPI.model";
import type { CartSize, CartTopping } from "@/types/order";

/**
 * Join the three catalogue endpoints into the options a dish actually offers.
 *
 * Sizes need all three: the dish lists its size ids, /dish-sizes supplies the
 * names, and the dish's own `priceToDishSizeId` supplies the prices. A size
 * with no price entry is dropped rather than silently charged at the base
 * price — better to offer no choice than the wrong amount.
 */
export const resolveSizesForDish = (
  dish: Dish,
  sizes: DishSize[],
): CartSize[] =>
  (dish.dishSizeIds ?? []).flatMap((sizeId) => {
    const size = sizes.find((candidate) => candidate.id === sizeId);
    const priced = dish.priceToDishSizeId?.find((entry) => entry.id === sizeId);
    if (!size || priced === undefined) return [];
    return [
      { id: size.id, translations: size.translations, price: priced.price },
    ];
  });

/** Toppings offered by this dish, at quantity 1 (v1 has no topping stepper). */
export const resolveToppingsForDish = (
  dish: Dish,
  toppings: Topping[],
): CartTopping[] =>
  (dish.dishToppingIds ?? []).flatMap((toppingId) => {
    const topping = toppings.find((candidate) => candidate.id === toppingId);
    if (!topping) return [];
    return [
      {
        id: topping.id,
        translations: topping.translations,
        price: topping.price,
        quantity: 1,
      },
    ];
  });
